import { Menu, X, LogOut, KeyRound } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { wsClient } from "../../../import";
import { logoutUser } from "../../../store/admin/auth-slice";
import ConfirmationDialog from "./confirmationDialog";
import { usePopover } from "../../../hooks/usePopover";
import KycExpiredBell from "./kycExpiredBell";
import ChatNavButton from "../../common/chat/chatNavButton";

interface NavbarProps {
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
}

const Navbar = ({ toggleSidebar, isSidebarOpen }: NavbarProps) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const dropdownRef = usePopover<HTMLDivElement>({ isOpen: isDropdownOpen, onClose: () => setIsDropdownOpen(false) });
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(logoutUser());
    setIsDropdownOpen(false);
    wsClient.disconnect();
    navigate("/login");
  };

  return (
    <nav className="flex items-center justify-between px-4 py-1.5 bg-[#100e0a] border-b border-[#BBA473]/15 z-25">
      {/* Hamburger toggle — always visible on mobile/tablet */}
      <button
        onClick={toggleSidebar}
        className="flex items-center space-x-2 text-white font-medium lg:hidden"
        aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        <span>{isSidebarOpen ? "Close" : "Menu"}</span>
      </button>

      {/* Spacer so avatar stays right-aligned when no hamburger is shown */}
      <div className="hidden lg:block" />

      <div className="flex items-center space-x-4 ml-auto">
        <ChatNavButton />
        <KycExpiredBell />
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="hover:opacity-80 transition-opacity flex items-center space-x-2"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={isDropdownOpen}
          >
            <div className="relative inline-flex items-center justify-center w-10 h-10 overflow-hidden bg-[#BBA473] rounded-full cursor-pointer">
              <span className="font-medium text-black">
                {`${user?.firstName?.[0] ?? ""}${user?.surname?.[0] ?? ""}`}
              </span>
            </div>
          </button>

          {isDropdownOpen && (
            <div
              role="menu"
              aria-label="Account menu"
              className="absolute right-0 mt-2 w-48 bg-[#BBA473] rounded-md shadow-lg z-50"
            >
              <Link
                to="/settings/change-password"
                role="menuitem"
                className="flex items-center hover:bg-gradient-to-r hover:from-[#685A3D] hover:to-[#BBA473] hover:text-white space-x-2 px-4 py-2 text-sm text-black w-full cursor-pointer"
                onClick={() => setIsDropdownOpen(false)}
              >
                <KeyRound size={18} />
                <span>Password</span>
              </Link>
              <button
                onClick={() => setLogoutDialogOpen(true)}
                role="menuitem"
                className="flex items-center hover:bg-gradient-to-r hover:from-[#685A3D] hover:to-[#BBA473] hover:text-white space-x-2 px-4 py-2 text-sm text-black w-full cursor-pointer"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogout}
        title="Log Out"
        message="Are you sure you want to log out?"
        confirmText="Log Out"
        cancelText="Cancel"
      />
    </nav>
  );
};

export default Navbar;